import { cn } from '@/lib/utils';

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

interface InvoiceDetailCardProps {
  invoice: {
    status: string;
    dueDate: string;
    currency: string;
    createdAt: number;
    paidAt?: number | null;
  };
  lineItems: LineItem[];
  isOverdue: boolean;
  config: { label: string; color: string };
  formatCurrency: (cents: number, currency: string) => string;
  formatDate: (dateStr: string | number) => string;
}

export function InvoiceDetailCard({
  invoice,
  lineItems,
  isOverdue,
  config,
  formatCurrency,
  formatDate,
}: InvoiceDetailCardProps) {
  return (
    <div className="p-4 bg-card border-t border-border">
      {lineItems.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Line Items</h4>
          <div className="space-y-2">
            {lineItems.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-sm py-2 border-b last:border-0"
              >
                <div>
                  <span className="text-foreground">{item.description}</span>
                  <span className="text-muted-foreground ml-2">x{item.quantity}</span>
                </div>
                <span className="text-foreground">
                  {formatCurrency(item.unitPrice * item.quantity, invoice.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-muted-foreground">Created</p>
          <p className="text-foreground">{formatDate(invoice.createdAt)}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Due Date</p>
          <p className={cn(
            'font-medium',
            isOverdue ? 'text-red-600' : 'text-foreground'
          )}>
            {formatDate(invoice.dueDate)}
          </p>
        </div>
        {invoice.paidAt && (
          <div>
            <p className="text-muted-foreground">Paid On</p>
            <p className="text-green-600 font-medium">
              {formatDate(invoice.paidAt)}
            </p>
          </div>
        )}
        <div>
          <p className="text-muted-foreground">Status</p>
          <p className={cn('font-medium', config.color)}>
            {isOverdue ? 'Overdue' : config.label}
          </p>
        </div>
      </div>

      {(invoice.status === 'sent' || invoice.status === 'overdue') && (
        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
          <p className="text-sm text-foreground">
            To make a payment, please contact your tennis academy directly.
          </p>
        </div>
      )}
    </div>
  );
}
